import type { components } from '@fluencelabs/vodopad-admin-api'
import { useCustom } from '@refinedev/core'
import { Card, DatePicker, Statistic, Table, Typography } from 'antd'
import type { Dayjs } from 'dayjs'
import { useState } from 'react'

import { FormattedDate } from '@/components/common/FormattedDate'
import { MonoId } from '@/components/common/MonoId'
import { VODOPAD_ENDPOINT } from '@/constants/constants'
import type { PaginatedItems } from '@/types/api'

type ChargeDto = components['schemas']['ChargeDto']
type TotalChargesResponse = components['schemas']['TotalChargesResponse']

export function ResourceBillingTab({
  userId,
  resourceId,
}: {
  userId: string
  resourceId: string
}) {
  const [dates, setDates] = useState<[Dayjs, Dayjs] | null>(null)

  const chargesQuery = useCustom<PaginatedItems<ChargeDto>>({
    url: `${VODOPAD_ENDPOINT}/v1/admin/users/${userId}/billing/charges`,
    method: 'get',
    config: {
      query: {
        page: 1,
        perPage: 50,
        resourceId,
        ...(dates?.[0] ? { startDate: dates[0].format('YYYY-MM-DD') } : {}),
        ...(dates?.[1] ? { endDate: dates[1].format('YYYY-MM-DD') } : {}),
      },
    },
  })

  const totalQuery = useCustom<TotalChargesResponse>({
    url: `${VODOPAD_ENDPOINT}/v1/admin/users/${userId}/billing/charges/total`,
    method: 'get',
    config: {
      query: {
        resourceId,
        ...(dates?.[0] ? { startDate: dates[0].format('YYYY-MM-DD') } : {}),
        ...(dates?.[1] ? { endDate: dates[1].format('YYYY-MM-DD') } : {}),
      },
    },
  })

  const charges =
    (chargesQuery.query.data?.data as PaginatedItems<ChargeDto> | undefined)
      ?.items ?? []

  const total = (
    totalQuery.query.data?.data as TotalChargesResponse | undefined
  )?.total

  return (
    <div>
      <Card size="small" style={{ marginBottom: 12, maxWidth: 260 }}>
        <Statistic
          title={dates ? 'Charged in period' : 'Charged in total'}
          value={total === undefined ? '—' : `$${Number(total).toFixed(2)}`}
          valueRender={
            totalQuery.query.isError
              ? () => (
                  <Typography.Text type="danger">
                    Failed to load
                  </Typography.Text>
                )
              : undefined
          }
          loading={totalQuery.query.isLoading}
        />
      </Card>
      <div style={{ marginBottom: 12 }}>
        <DatePicker.RangePicker
          onChange={(d) => setDates(d as [Dayjs, Dayjs] | null)}
          allowClear
        />
      </div>
      <Table
        loading={chargesQuery.query.isLoading}
        dataSource={charges}
        rowKey="id"
        columns={[
          {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            render: (id: string) => <MonoId id={id} />,
          },
          {
            title: 'Cost',
            dataIndex: 'cost',
            key: 'cost',
            width: 120,
            render: (v: string) => `$${Number(v).toFixed(2)}`,
          },
          {
            title: 'Charge At',
            dataIndex: 'chargeAt',
            key: 'chargeAt',
            render: (d: string | null) =>
              d ? <FormattedDate date={d} fmt="PPp" /> : '—',
          },
          {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (d: string) => <FormattedDate date={d} fmt="PPp" />,
          },
        ]}
        pagination={{ pageSize: 10 }}
      />
    </div>
  )
}
